import { FetchPolicy } from '@apollo/client'
import {
  ExpensablesDataTableDocument,
  MyGroupedExpensablesDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  CreateExpensableDocument,
  CreateExpensableMutationVariables,
  CreateExpenseFromDocumentDocument,
  CreateExpenseFromDocumentMutationVariables,
  CreateExpensesExpensableTaxesInput,
  CreateMileageDocument,
  CreateMileageMutationVariables,
  CreatePerDiemDocument,
  CreatePerDiemMutationVariables,
  DeleteExpensableDocument,
  DeleteExpensableMutationVariables,
  ExpensableExpenseFragment,
  ExpensableMileageFragment,
  ExpensablesDataCollectionDocument,
  ExpensablesPendingApprovalWidgetDocument,
  ExpensablesStatusCountsDocument,
  ExpensesApprovedWidgetDocument,
  ExpensesInsightsDocument,
  GetExpensablesDocument,
  GetExpensablesQueryVariables,
  GetExpensesCountDocument,
  GetGroupsDocument,
  MyGroupedExpensablesDataCollectionDocument,
  ReimbursementsApprovedWidgetDocument,
  ReimbursementsPendingApprovalWidgetDocument,
  SendExpensableForApprovalDocument,
  SendExpensableForApprovalMutationVariables,
  SendMileageForReviewDocument,
  SendMileageForReviewMutationVariables,
  UpdateAndApproveExpensableDocument,
  UpdateAndApproveExpensableMutationVariables,
  UpdateExpensableDocument,
  UpdateExpensableFromDocumentDocument,
  UpdateExpensableFromDocumentMutationVariables,
  UpdateExpensableMutationVariables,
  UpdateMileageDocument,
  UpdateMileageMutationVariables,
} from 'generated/resources/graphql'
import 'generated/resources/graphql'
import moment from 'moment'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'

import { CustomizationField, FieldTypes, Tax, fieldType } from './useExpensesCustomization'

export type ExpensableExpense = ExpensableExpenseFragment
export type ExpensableMileage = ExpensableMileageFragment
export enum ExpensableResourceType {
  Expense = 'Expense',
  Mileage = 'Mileage',
  PerDiem = 'PerDiem',
}

export type UseExpensables = Omit<
  GetExpensablesQueryVariables,
  'includeProjects' | 'includeTransactions' | 'includeJournalEntries' | 'includeSyncableState'
> & {
  includeProjects?: boolean
  includeTransactions?: boolean
  skip?: boolean
  fetchPolicy?: FetchPolicy
  includeJournalEntries?: boolean
  includeSyncableState?: boolean
  marketplaceIntegrationId?: number | null
}

export type UseExpensable = Omit<UseExpensables, 'ids' | 'employeeIds' | 'fetchPolicy'> & {
  id?: number
  resourceType?: ExpensableResourceType
  resourceId?: number
  employeeId?: number
}

export const normalizeEffectiveOn = (effectiveOn: string): string => {
  const date = moment(effectiveOn).toDate()
  const day = date.getDate()
  const month = date.getMonth()
  const year = date.getFullYear()

  // We want to force the date to be at 12:00 UTC, instead of the 00:00 UTC with the localtime offset.
  // This is because the date is used to calculate the effectiveOn date, and we want to avoid any timezone issues.
  // If we apply the +2gmt offset to the date, but we recover the date with the +1gmt offset, the date will be the previous day.
  // With 12:00 UTC, we could reduce the risk of timezone issues (+-12 hours margin).
  const newDatetime = moment.utc([year, month, day, 12])
  return dateToString(newDatetime, 'backend_datetime')
}

export const normalizeTaxes = ({
  taxes,
  vatAmount,
  customizationFields,
}: {
  taxes: Tax[]
  vatAmount: number | null
  customizationFields: CustomizationField[]
}): CreateExpensesExpensableTaxesInput[] => {
  const type = fieldType(customizationFields, 'expense', 'taxes')

  if (type === FieldTypes.cents && vatAmount) {
    return [{ amount: vatAmount }]
  } else if (type === FieldTypes.tax_type) {
    return taxes
      .map(({ base_amount, amount, percentage }) => ({
        baseAmount: base_amount,
        percentage: percentage != null ? percentage / 100 : null, //we send percentages as basis point(https://en.wikipedia.org/wiki/Basis_point)
        amount,
      }))
      .filter((tax) => Object.values(tax).some(Boolean))
  } else {
    return []
  }
}
export const useCreateFromDocument = () => {
  const [createExpensableFromDocument, { loading }] = useMutation(
    CreateExpenseFromDocumentDocument,
    {
      extractorFn: (data) => data.expensesMutations.expensables,
      refetchQueries: [
        ExpensablesDataTableDocument,
        ExpensablesDataCollectionDocument,
        ExpensablesStatusCountsDocument,
        GetGroupsDocument,
        MyGroupedExpensablesDataTableDocument,
        MyGroupedExpensablesDataCollectionDocument,
      ],
      awaitRefetchQueries: true,
      onQueryUpdated: (query) => query.reobserve(),
    }
  )

  return {
    loading,
    createFromDocument: (variables: CreateExpenseFromDocumentMutationVariables) =>
      createExpensableFromDocument({ variables }),
  }
}

export const useCreate = () => {
  const [createExpensable, { loading }] = useMutation(CreateExpensableDocument, {
    extractorFn: (data) => data.expensesMutations.createExpensesExpensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetExpensesCountDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      ExpensesApprovedWidgetDocument,
      ReimbursementsPendingApprovalWidgetDocument,
      ReimbursementsApprovedWidgetDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    create: (variables: CreateExpensableMutationVariables) => createExpensable({ variables }),
    loading,
  }
}

export const useCreatePerDiem = () => {
  const [createPerDiem, { loading }] = useMutation(CreatePerDiemDocument, {
    extractorFn: (data) => data.expensesMutations.expensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetExpensesCountDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    createPerDiem: (variables: CreatePerDiemMutationVariables) => createPerDiem({ variables }),
    loading,
  }
}

export const useCreateMileage = () => {
  const [createMileage, { loading }] = useMutation(CreateMileageDocument, {
    extractorFn: (data) => data.expensesMutations.expensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetExpensesCountDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    createMileage: (variables: CreateMileageMutationVariables) => createMileage({ variables }),
    loading,
  }
}

export const useUpdateMileage = () => {
  const [updateMileage, { loading }] = useMutation(UpdateMileageDocument, {
    extractorFn: (data) => data.expensesMutations.expensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    updateMileage: (variables: UpdateMileageMutationVariables) => updateMileage({ variables }),
    loading,
  }
}

export const useSendMileageForApproval = () => {
  const [sendMileageForApproval, { loading }] = useMutation(SendMileageForReviewDocument, {
    extractorFn: (data) => data.expensesMutations.expensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetExpensesCountDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      ExpensesApprovedWidgetDocument,
      ReimbursementsPendingApprovalWidgetDocument,
      ReimbursementsApprovedWidgetDocument,
      ExpensesInsightsDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    sendMileageForApproval: (variables: SendMileageForReviewMutationVariables) =>
      sendMileageForApproval({ variables }),
    loading,
  }
}

export const useSendForApproval = () => {
  const [sendExpensableForApproval, { loading }] = useMutation(SendExpensableForApprovalDocument, {
    extractorFn: (data) => data.expensesMutations.readyToReviewExpensesExpensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetExpensesCountDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      ExpensesApprovedWidgetDocument,
      ReimbursementsPendingApprovalWidgetDocument,
      ReimbursementsApprovedWidgetDocument,
      ExpensesInsightsDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    sendForApproval: (variables: SendExpensableForApprovalMutationVariables) =>
      sendExpensableForApproval({ variables }),
    loading,
  }
}

export const useUpdate = () => {
  const [updateExpensable, { loading }] = useMutation(UpdateExpensableDocument, {
    extractorFn: (data) => data.expensesMutations.updateExpensesExpensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    update: (variables: UpdateExpensableMutationVariables) => updateExpensable({ variables }),
    loading,
  }
}

export const useUpdateAndApprove = () => {
  const [updateAndApproveExpensable, { loading }] = useMutation(
    UpdateAndApproveExpensableDocument,
    {
      extractorFn: (data) => data.expensesMutations.updateAndApproveExpensesExpensable,
      refetchQueries: [
        ExpensablesDataTableDocument,
        ExpensablesDataCollectionDocument,
        ExpensablesStatusCountsDocument,
        GetGroupsDocument,
        MyGroupedExpensablesDataTableDocument,
        MyGroupedExpensablesDataCollectionDocument,
        GetExpensesCountDocument,
        ExpensesApprovedWidgetDocument,
        ReimbursementsPendingApprovalWidgetDocument,
        ReimbursementsApprovedWidgetDocument,
        ExpensablesPendingApprovalWidgetDocument,
      ],
      awaitRefetchQueries: true,
      onQueryUpdated: (query) => query.reobserve(),
    }
  )

  return {
    updateAndApprove: (variables: UpdateAndApproveExpensableMutationVariables) =>
      updateAndApproveExpensable({ variables }),
    loading,
  }
}

export const useUpdateFromDocument = () => {
  const [updateExpensableFromDocument, { loading }] = useMutation(
    UpdateExpensableFromDocumentDocument,
    {
      extractorFn: (data) => data.expensesMutations.updateFromDocumentExpensesExpensable,
      refetchQueries: [
        ExpensablesDataTableDocument,
        ExpensablesDataCollectionDocument,
        ExpensablesStatusCountsDocument,
        GetGroupsDocument,
        MyGroupedExpensablesDataTableDocument,
        MyGroupedExpensablesDataCollectionDocument,
      ],
      awaitRefetchQueries: true,
      onQueryUpdated: (query) => query.reobserve(),
    }
  )

  return {
    updateFromDocument: (variables: UpdateExpensableFromDocumentMutationVariables) =>
      updateExpensableFromDocument({ variables }),
    loading,
  }
}

export const useDelete = () => {
  const [deleteExpensable, { loading }] = useMutation(DeleteExpensableDocument, {
    extractorFn: (data) => data.expensesMutations.deleteExpensesExpensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
  })

  return {
    delete: (variables: DeleteExpensableMutationVariables) => deleteExpensable({ variables }),
    loading,
  }
}

export const useExpensables = ({
  skip,
  fetchPolicy,
  includeProjects = false,
  includeTransactions = false,
  includeJournalEntries = false,
  includeCostCenterDistribution = false,
  includeSyncableState = false,
  marketplaceIntegrationId = null,
  ...variables
}: UseExpensables) => {
  const { data, loading, refetch } = useQuery(GetExpensablesDocument, {
    variables: {
      ...variables,
      includeProjects,
      includeTransactions,
      includeJournalEntries,
      includeCostCenterDistribution,
      includeSyncableState,
      marketplaceIntegrationId,
    },
    fetchPolicy,
    skip,
  })

  return {
    loading,
    expensables: data?.expenses?.expensablesConnection?.nodes ?? [],
    refetch,
  }
}

export const useExpensable = ({
  id,
  resourceType,
  resourceId,
  employeeId,
  includeTransactions,
  includeJournalEntries = false,
  includeCostCenterDistribution = false,
  includeSyncableState = false,
  marketplaceIntegrationId = null,
  skip,
  ...variables
}: UseExpensable) => {
  const ids = id ? [id] : null
  const byResources = resourceType && resourceId ? [{ resourceType, id: resourceId }] : null
  const employeeIds = employeeId ? [employeeId] : null

  const { expensables, loading, refetch } = useExpensables({
    ...variables,
    ids,
    byResources,
    employeeIds,
    includeTransactions: includeTransactions,
    includeJournalEntries: includeJournalEntries,
    includeCostCenterDistribution: includeCostCenterDistribution,
    includeSyncableState: includeSyncableState,
    marketplaceIntegrationId: marketplaceIntegrationId,
    skip: skip || (!ids && !byResources),
    fetchPolicy: 'network-only',
  })

  return { loading, refetch, expensable: expensables[0] }
}
